import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import FormControl from '@zenbusiness/zui/FormControl';
import ZuiTextField from '@zenbusiness/zui/TextField';

import FieldHeading from './FieldHeading';

/**
 * Unified text input field - handles both single-line and multiline text input
 * @param {object} props - Component props
 * @param {object} props.def - Field definition
 * @param {string} props.value - Current text value
 * @param {Function} props.onChange - Change handler
 * @param {boolean} [props.disabled=false] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 */
function TextInputField({ def, value, onChange, disabled = false, error }) {
  const handleChange = (event) => {
    onChange(event.target.value);
  };

  const fieldId = `text-input-${def.id}`;
  const hasError = Boolean(error);
  const isMultiline = def.multiline || def.type === 'text';

  return (
    <FormControl
      component="fieldset"
      disabled={disabled}
      error={hasError}
      fullWidth
      required={def.required}
    >
      <FieldHeading
        description={def.description}
        label={def.label}
        labelId={`${fieldId}-label`}
        required={def.required}
      />

      <Box mt={2}>
        <ZuiTextField
          disabled={disabled}
          error={hasError}
          fullWidth
          helperText={error || def.helpText}
          id={fieldId}
          inputProps={{
            maxLength: def.validate?.maxLength,
            pattern: def.validate?.pattern
          }}
          multiline={isMultiline}
          placeholder={def.placeholder}
          required={def.required}
          rows={isMultiline ? 4 : 1}
          value={value || ''}
          variant="outlined"
          onChange={handleChange}
        />
      </Box>
    </FormControl>
  );
}

TextInputField.propTypes = {
  def: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['input', 'text']).isRequired,
    label: PropTypes.string,
    description: PropTypes.string,
    helpText: PropTypes.string,
    required: PropTypes.bool,
    multiline: PropTypes.bool,
    placeholder: PropTypes.string,
    validate: PropTypes.shape({
      maxLength: PropTypes.number,
      pattern: PropTypes.string
    })
  }).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string
};

export default TextInputField;
